import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaEnvelope,
  FaEdit,
  FaMapMarkerAlt,
  FaPhone,
  FaPrint,
  FaReceipt,
  FaSave,
  FaTrash,
  FaUser
} from '../../../utils/iconUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { programService, ProgramEvent } from '../../../firebase/programFirestoreService';
import {
  Accounting,
  createAccountingRecord,
  deleteAccountingRecord,
  updateAccountingRecord
} from '../../../firebase/accountingFirestoreService';
import { useUI } from '../../../components/common/UIMessageProvider';

type EventFormState = {
  title: string;
  fromDate: string;
  toDate: string;
  venue: string;
  organizer: string;
  category: string;
  attendeeName: string;
  attendeePhone: string;
  attendeeEmail: string;
  amount: string;
  paymentMethod: string;
  notes: string;
  status: ProgramEvent['status'];
};

const toDateTimeInputValue = (date = new Date()) => {
  const copy = new Date(date);
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
  return copy.toISOString().slice(0, 16);
};

const createReceiptNumber = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const tail = String(now.getTime()).slice(-6);
  return `AIFCC-${year}${month}${day}-${tail}`;
};

const formatEventDate = (eventDate?: Timestamp) => {
  const date = eventDate?.toDate?.();
  if (!date) return 'Not set';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatEventPeriod = (eventDate?: Timestamp, endDate?: Timestamp) => {
  const from = formatEventDate(eventDate);
  const to = formatEventDate(endDate);
  if (!endDate) return from;
  return `${from} to ${to}`;
};

const formatAmount = (amount?: number) => {
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS'
  }).format(Number(amount) || 0);
};

const eventStatusToAccountingStatus = (status: ProgramEvent['status']): Accounting['status'] => {
  if (status === 'completed') return 'Paid';
  if (status === 'cancelled') return 'Rejected';
  return 'Pending';
};

const timestampToDateInput = (timestamp?: Timestamp) => {
  return timestamp?.toDate?.().toISOString().split('T')[0] || new Date().toISOString().split('T')[0];
};

const emptyForm = (): EventFormState => ({
  title: '',
  fromDate: toDateTimeInputValue(),
  toDate: toDateTimeInputValue(new Date(Date.now() + 2 * 60 * 60 * 1000)),
  venue: '',
  organizer: 'Alhaji Inusah Foundation',
  category: 'Community Event',
  attendeeName: '',
  attendeePhone: '',
  attendeeEmail: '',
  amount: '0',
  paymentMethod: 'Cash',
  notes: '',
  status: 'planned'
});

const formFromEvent = (event: ProgramEvent): EventFormState => ({
  title: event.title || '',
  fromDate: toDateTimeInputValue(event.eventDate?.toDate?.() || new Date()),
  toDate: toDateTimeInputValue(event.endDate?.toDate?.() || event.eventDate?.toDate?.() || new Date()),
  venue: event.venue || '',
  organizer: event.organizer || 'Alhaji Inusah Foundation',
  category: event.category || 'Community Event',
  attendeeName: event.attendeeName || '',
  attendeePhone: event.attendeePhone || '',
  attendeeEmail: event.attendeeEmail || '',
  amount: String(event.amount ?? 0),
  paymentMethod: event.paymentMethod || 'Cash',
  notes: event.notes || '',
  status: event.status || 'planned'
});

const eventCategories = [
  'Community Event',
  'Marriage',
  'Outreach',
  'Donation',
  'Iftar',
  'Workshop',
  'Training',
  'Conference',
  'Seminar',
  'Education Support',
  'Health Screening',
  'Youth Program',
  'Women Empowerment',
  'Religious Program',
  'Library Program',
  'Scholarship',
  'Fundraising',
  'Volunteer Activity',
  'Emergency Support',
  'Other'
];

const buildAccountingRecord = (event: ProgramEvent, userId?: string): Accounting => ({
  accountingId: `EVT-${event.receiptNumber}`,
  description: `Event receipt: ${event.title} - received from ${event.attendeeName}`,
  amount: Number(event.amount) || 0,
  currency: 'GHS',
  category: `Event Receipt - ${event.category}`,
  paymentMethod: event.paymentMethod || 'Cash',
  vendor: event.attendeeName,
  receiptNumber: event.receiptNumber,
  date: timestampToDateInput(event.eventDate),
  status: eventStatusToAccountingStatus(event.status),
  notes: [
    `Event period: ${formatEventPeriod(event.eventDate, event.endDate)}`,
    `Venue: ${event.venue}`,
    event.notes ? `Notes: ${event.notes}` : ''
  ].filter(Boolean).join('\n'),
  createdBy: userId || ''
});

const ProgramEvents: React.FC = () => {
  const auth = useAuth();
  const ui = useUI();
  const canManageEvents = auth.canPerformAction('programs', 'edit');
  const logoSrc = `${process.env.PUBLIC_URL || ''}/Official%20logo.png`;

  const [events, setEvents] = useState<ProgramEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<ProgramEvent | null>(null);
  const [editingEvent, setEditingEvent] = useState<ProgramEvent | null>(null);
  const [form, setForm] = useState<EventFormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const receiptEvent = useMemo(() => selectedEvent || events[0] || null, [selectedEvent, events]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await programService.getEvents();
      setEvents(data);
      setSelectedEvent((current) => current || data[0] || null);
    } catch (error) {
      console.error('Error loading events:', error);
      ui.showMessage('Failed to load events', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [ui]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const updateField = (field: keyof EventFormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setEditingEvent(null);
    setForm(emptyForm());
  };

  const handleEdit = (eventToEdit: ProgramEvent) => {
    setEditingEvent(eventToEdit);
    setSelectedEvent(eventToEdit);
    setForm(formFromEvent(eventToEdit));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canManageEvents) {
      ui.showMessage('You do not have permission to add events', { type: 'error' });
      return;
    }

    if (!form.title.trim() || !form.fromDate || !form.toDate || !form.venue.trim() || !form.attendeeName.trim()) {
      ui.showMessage('Please fill the event title, from date, to date, venue, and receipt name', { type: 'error' });
      return;
    }

    const fromDate = new Date(form.fromDate);
    const toDate = new Date(form.toDate);
    if (toDate < fromDate) {
      ui.showMessage('The To date must be after the From date', { type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const receiptNumber = editingEvent?.receiptNumber || createReceiptNumber();
      const eventData: Omit<ProgramEvent, 'id' | 'createdAt' | 'updatedAt'> = {
        title: form.title.trim(),
        eventDate: Timestamp.fromDate(fromDate),
        endDate: Timestamp.fromDate(toDate),
        venue: form.venue.trim(),
        organizer: form.organizer.trim() || 'Alhaji Inusah Foundation',
        category: form.category.trim() || 'Community Event',
        attendeeName: form.attendeeName.trim(),
        attendeePhone: form.attendeePhone.trim(),
        attendeeEmail: form.attendeeEmail.trim(),
        amount: Number(form.amount) || 0,
        paymentMethod: form.paymentMethod,
        notes: form.notes.trim(),
        receiptNumber,
        status: form.status,
        accountingRecordId: editingEvent?.accountingRecordId || '',
        createdBy: editingEvent?.createdBy || auth.currentUser?.uid || ''
      };

      if (editingEvent?.id) {
        const accountingPayload = buildAccountingRecord({ ...editingEvent, ...eventData }, auth.currentUser?.uid);
        let accountingRecordId = editingEvent.accountingRecordId || '';

        if (accountingRecordId) {
          const accountingResult = await updateAccountingRecord(accountingRecordId, accountingPayload);
          if (!accountingResult.success) {
            throw new Error(accountingResult.error || 'Failed to update accounting record');
          }
        } else {
          const accountingResult = await createAccountingRecord(accountingPayload);
          if (!accountingResult.success || !accountingResult.id) {
            throw new Error(accountingResult.error || 'Failed to create accounting record');
          }
          accountingRecordId = accountingResult.id;
        }

        const updatePayload = { ...eventData, accountingRecordId };
        const result = await programService.updateEvent(editingEvent.id, updatePayload);
        if (!result.success) {
          throw new Error((result as any).error || 'Failed to update event receipt');
        }

        const updatedEvent: ProgramEvent = {
          ...editingEvent,
          ...updatePayload,
          updatedAt: Timestamp.fromDate(new Date())
        };

        setEvents((current) => current.map((item) => item.id === editingEvent.id ? updatedEvent : item));
        setSelectedEvent(updatedEvent);
        resetForm();
        ui.showMessage('Event receipt updated and synced to accounting', { type: 'success' });
        return;
      }

      const accountingResult = await createAccountingRecord(buildAccountingRecord(eventData as ProgramEvent, auth.currentUser?.uid));
      if (!accountingResult.success || !accountingResult.id) {
        throw new Error(accountingResult.error || 'Failed to create accounting record');
      }

      const eventWithAccounting = {
        ...eventData,
        accountingRecordId: accountingResult.id
      };

      const id = await programService.createEvent(eventWithAccounting);
      const savedEvent: ProgramEvent = {
        ...eventWithAccounting,
        id,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      setEvents((current) => [savedEvent, ...current]);
      setSelectedEvent(savedEvent);
      resetForm();
      ui.showMessage('Event receipt created and saved to accounting', { type: 'success' });
    } catch (error) {
      console.error('Error saving event:', error);
      ui.showMessage(editingEvent ? 'Failed to update event receipt' : 'Failed to create event receipt', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId?: string) => {
    if (!eventId) return;
    const eventToDelete = events.find((item) => item.id === eventId);
    const ok = await ui.askConfirm({
      title: 'Delete Event',
      message: 'Delete this event receipt permanently? The linked accounting record will also be removed.',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!ok) return;

    if (eventToDelete?.accountingRecordId) {
      const accountingDelete = await deleteAccountingRecord(eventToDelete.accountingRecordId);
      if (!accountingDelete.success) {
        ui.showMessage('Failed to delete linked accounting record', { type: 'error' });
        return;
      }
    }

    const result = await programService.deleteEvent(eventId);
    if (result.success) {
      setEvents((current) => current.filter((item) => item.id !== eventId));
      if (selectedEvent?.id === eventId) setSelectedEvent(null);
      ui.showMessage('Event deleted', { type: 'success' });
    } else {
      ui.showMessage('Failed to delete event', { type: 'error' });
    }
  };

  const handlePrint = (eventToPrint?: ProgramEvent) => {
    if (eventToPrint) setSelectedEvent(eventToPrint);
    window.setTimeout(() => {
      const receiptElement = document.getElementById('event-receipt-print');
      if (!receiptElement) {
        window.print();
        return;
      }

      const printWindow = window.open('', '_blank', 'width=900,height=1100');
      if (!printWindow) {
        window.print();
        return;
      }

      const pageStyles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
        .map((node) => (node as HTMLElement).outerHTML)
        .join('\n');

      printWindow.document.open();
      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>${eventToPrint?.receiptNumber || selectedEvent?.receiptNumber || 'Receipt'}</title>
            ${pageStyles}
            <style>
              @page {
                size: A4;
                margin: 12mm;
              }
              html,
              body {
                margin: 0;
                padding: 0;
                background: #ffffff;
                color: #111827;
                font-family: Inter, Arial, sans-serif;
              }
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              * {
                box-sizing: border-box;
              }
              img {
                max-width: 100%;
              }
              #print-root {
                width: 100%;
                max-width: 760px;
                margin: 0 auto;
              }
              #print-root #event-receipt-print {
                border: 0 !important;
                box-shadow: none !important;
                border-radius: 0 !important;
              }
              @media print {
                html,
                body {
                  width: 210mm;
                  min-height: 0;
                  overflow: visible;
                }
                #print-root {
                  max-width: none;
                  margin: 0;
                }
                .shadow-soft {
                  box-shadow: none !important;
                }
              }
            </style>
          </head>
          <body>
            <div id="print-root">${receiptElement.outerHTML}</div>
            <script>
              function printReceipt() {
                window.focus();
                window.print();
                window.onafterprint = function () { window.close(); };
              }

              (function () {
                var images = Array.prototype.slice.call(document.images || []);
                if (!images.length) {
                  setTimeout(printReceipt, 100);
                  return;
                }

                var pending = images.length;
                var done = false;
                var finish = function () {
                  if (done) return;
                  pending -= 1;
                  if (pending <= 0) {
                    done = true;
                    setTimeout(printReceipt, 100);
                  }
                };

                images.forEach(function (image) {
                  if (image.complete) {
                    finish();
                    return;
                  }
                  image.onload = finish;
                  image.onerror = finish;
                });

                setTimeout(function () {
                  if (!done) {
                    done = true;
                    printReceipt();
                  }
                }, 1200);
              })();
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }, 80);
  };

  return (
    <div className="event-page-shell space-y-4">
      <style>
        {`
          @media print {
            body * {
              visibility: hidden !important;
            }
            #event-receipt-print, #event-receipt-print * {
              visibility: visible !important;
            }
            #event-receipt-print {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              box-shadow: none !important;
              border: 0 !important;
            }
            .no-print {
              display: none !important;
            }
            @page {
              margin: 0.45in;
            }
          }
        `}
      </style>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4">
        <section className="bg-white border border-gray-200 rounded-lg shadow-soft overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary-600 text-white">
                <FaCalendarAlt className="h-4 w-4" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-gray-900">{editingEvent ? 'Edit Event' : 'Add Event'}</h2>
                <p className="text-xs text-gray-500">
                  {editingEvent
                    ? `Correct receipt ${editingEvent.receiptNumber} and sync the accounting record.`
                    : 'Create an event, save it to accounting, and issue a printable receipt.'}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Event Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Youth leadership workshop"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">From Date & Time *</label>
                <input
                  type="datetime-local"
                  value={form.fromDate}
                  onChange={(e) => updateField('fromDate', e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">To Date & Time *</label>
                <input
                  type="datetime-local"
                  value={form.toDate}
                  onChange={(e) => updateField('toDate', e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Venue *</label>
                <input
                  type="text"
                  value={form.venue}
                  onChange={(e) => updateField('venue', e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Foundation Hall"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Organizer</label>
                <input
                  type="text"
                  value={form.organizer}
                  onChange={(e) => updateField('organizer', e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => updateField('category', e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 bg-white focus:border-primary-500 focus:ring-primary-500"
                >
                  {eventCategories.map((category) => (
                    <option key={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Receipt Status</label>
                <select
                  value={form.status}
                  onChange={(e) => updateField('status', e.target.value as ProgramEvent['status'])}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 bg-white focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="planned">Planned</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                <FaReceipt className="text-primary-600" />
                Receipt Details
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Received From *</label>
                  <input
                    type="text"
                    value={form.attendeeName}
                    onChange={(e) => updateField('attendeeName', e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Full name / organization"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => updateField('amount', e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={form.attendeePhone}
                    onChange={(e) => updateField('attendeePhone', e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={form.attendeeEmail}
                    onChange={(e) => updateField('attendeeEmail', e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <select
                    value={form.paymentMethod}
                    onChange={(e) => updateField('paymentMethod', e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 bg-white focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option>Cash</option>
                    <option>Mobile Money</option>
                    <option>Bank Transfer</option>
                    <option>Cheque</option>
                    <option>Complimentary</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => updateField('notes', e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Purpose, items covered, or additional receipt remarks"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-500">
                Saved events appear below and can be edited or printed again at any time.
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                {editingEvent && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel Edit
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving || !canManageEvents}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  ) : (
                    <FaSave className="h-4 w-4" />
                  )}
                  {editingEvent ? 'Update Event Receipt' : 'Save Event Receipt'}
                </button>
              </div>
            </div>
          </form>
        </section>

        <aside className="space-y-3">
          <div className="no-print flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Receipt Preview</h2>
            <button
              type="button"
              onClick={() => receiptEvent && handlePrint(receiptEvent)}
              disabled={!receiptEvent}
              className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
            >
              <FaPrint className="h-3.5 w-3.5" />
              Print
            </button>
          </div>

          <div id="event-receipt-print" className="bg-white border border-gray-200 rounded-lg shadow-soft overflow-hidden">
            {receiptEvent ? (
              <div className="bg-white p-7 text-[16px] leading-relaxed text-gray-900">
                <div className="overflow-hidden rounded-md border border-gray-300">
                  <div className="border-b-4 border-gray-950 px-6 py-5">
                    <div className="flex items-start justify-between gap-5">
                      <div className="flex items-center gap-4">
                        <img src={logoSrc} alt="Alhaji Inusah Foundation" className="h-16 w-16 object-contain" />
                        <div>
                          <h3 className="text-xl font-bold leading-snug text-gray-950">
                            Alhaji Inusah Foundation Community Center AIFCC
                          </h3>
                          <p className="mt-1 text-base font-bold uppercase tracking-[0.22em] text-gray-700">Receipt</p>
                        </div>
                      </div>
                      <div className="rounded border border-gray-300 px-3 py-2 text-right">
                        <p className="text-sm font-semibold uppercase text-gray-500">Receipt No.</p>
                        <p className="text-base font-bold text-gray-950">{receiptEvent.receiptNumber}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 border-b border-gray-300 bg-gray-50 text-sm text-gray-700">
                    <div className="border-r border-gray-300 px-5 py-3">
                      <p className="font-bold uppercase text-gray-500">Issued Date</p>
                      <p className="mt-1 font-semibold text-gray-950">{formatEventDate(receiptEvent.createdAt || Timestamp.fromDate(new Date()))}</p>
                    </div>
                    <div className="px-5 py-3 text-right">
                      <p className="font-bold uppercase text-gray-500">Status</p>
                      <p className="mt-1 font-semibold capitalize text-gray-950">{receiptEvent.status}</p>
                    </div>
                  </div>

                  <div className="px-6 py-5">
                    <div className="grid grid-cols-1 gap-4 border-b border-gray-200 pb-5">
                      <div className="flex items-start gap-3">
                        <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-md bg-gray-100 text-primary-700">
                          <FaCalendarAlt />
                        </span>
                        <div>
                          <p className="text-sm font-bold uppercase text-gray-500">Event</p>
                          <p className="text-lg font-bold text-gray-950">{receiptEvent.title}</p>
                          <p className="text-base text-gray-700">{formatEventPeriod(receiptEvent.eventDate, receiptEvent.endDate)}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-md bg-gray-100 text-primary-700">
                          <FaMapMarkerAlt />
                        </span>
                        <div>
                          <p className="text-sm font-bold uppercase text-gray-500">Venue</p>
                          <p className="text-lg font-bold text-gray-950">{receiptEvent.venue}</p>
                          <p className="text-base text-gray-700">{receiptEvent.organizer}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 overflow-hidden rounded-md border border-gray-300">
                      <div className="grid grid-cols-[minmax(0,1fr)_minmax(8.5rem,10rem)] border-b border-gray-300 bg-gray-950 px-4 py-3 text-base font-bold uppercase text-white">
                        <span>Description</span>
                        <span className="text-right">Amount</span>
                      </div>
                      <div className="grid grid-cols-[minmax(0,1fr)_minmax(8.5rem,10rem)]">
                        <div className="border-r border-gray-300 px-4 py-4">
                          <p className="text-lg font-bold text-gray-950">{receiptEvent.category}</p>
                          <p className="mt-1 text-base text-gray-700">Payment method: {receiptEvent.paymentMethod}</p>

                          <div className="mt-4 border-t border-gray-200 pt-4">
                            <p className="text-sm font-bold uppercase text-gray-500">Received From</p>
                            <p className="mt-1 text-lg font-bold text-gray-950">{receiptEvent.attendeeName}</p>
                            <div className="mt-2 space-y-1 text-base text-gray-700">
                              {receiptEvent.attendeePhone && (
                                <div className="flex items-center gap-2">
                                  <FaPhone className="text-primary-600" />
                                  <span>{receiptEvent.attendeePhone}</span>
                                </div>
                              )}
                              {receiptEvent.attendeeEmail && (
                                <div className="flex items-center gap-2">
                                  <FaEnvelope className="text-primary-600" />
                                  <span>{receiptEvent.attendeeEmail}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="px-4 py-4 text-right text-lg font-bold text-gray-950">
                          {formatAmount(receiptEvent.amount)}
                        </div>
                      </div>
                      <div className="grid grid-cols-[minmax(0,1fr)_minmax(8.5rem,10rem)] border-t border-gray-300 bg-gray-100 px-4 py-3">
                        <span className="text-lg font-bold text-gray-950">Total</span>
                        <span className="text-right text-xl font-bold text-gray-950">{formatAmount(receiptEvent.amount)}</span>
                      </div>
                    </div>

                    {receiptEvent.notes && (
                      <div className="mt-5 rounded-md border border-dashed border-gray-300 p-4">
                        <p className="text-sm font-bold uppercase text-gray-500">Notes</p>
                        <p className="mt-1 text-base text-gray-700">{receiptEvent.notes}</p>
                      </div>
                    )}

                    <div className="mt-10 space-y-12">
                      <p className="text-center text-lg font-bold text-gray-800">Thank you</p>
                      <div className="grid grid-cols-2 gap-10 text-center text-base font-bold text-gray-800">
                        <div className="col-span-2 mx-auto w-64 border-t border-gray-500 pt-3">
                          Administrator Signature
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-gray-500">
                Save an event to preview its receipt.
              </div>
            )}
          </div>
        </aside>
      </div>

      <section className="bg-white border border-gray-200 rounded-lg shadow-soft overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-gray-200 bg-slate-50 px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Recent Events</h2>
            <p className="text-xs text-gray-500">Select an event to preview or print its receipt.</p>
          </div>
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 border border-gray-200">
            <FaCheckCircle className="text-green-600" />
            {events.length} saved
          </span>
        </div>

        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No events created yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Event</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Received From</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Receipt</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Amount</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {events.map((item) => (
                  <tr key={item.id} className={selectedEvent?.id === item.id ? 'bg-primary-50/70' : 'hover:bg-gray-50'}>
                    <td className="px-3 py-3">
                      <button type="button" onClick={() => setSelectedEvent(item)} className="text-left">
                        <span className="block font-semibold text-gray-900">{item.title}</span>
                        <span className="block text-xs text-gray-500">{formatEventPeriod(item.eventDate, item.endDate)} at {item.venue}</span>
                      </button>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <FaUser className="text-gray-400" />
                        {item.attendeeName}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-700">{item.receiptNumber}</td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-900">{formatAmount(item.amount)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedEvent(item);
                            handlePrint(item);
                          }}
                          className="inline-flex items-center gap-1 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-gray-800"
                        >
                          <FaPrint className="h-3 w-3" />
                          Print
                        </button>
                        {canManageEvents && (
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                          >
                            <FaEdit className="h-3 w-3" />
                            Edit
                          </button>
                        )}
                        {canManageEvents && (
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                          >
                            <FaTrash className="h-3 w-3" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProgramEvents;
